'use client';

import React, { useState } from 'react';
import Link from 'next/link';

// ─── JUSTICE CATEGORY BADGES ──────────────────────────────────────────────────
const JUSTICE_CATEGORIES = [
  { id: 'legal', label: 'LEGAL SUPPORT', color: '#8b4513', bg: 'rgba(139,69,19,0.12)', border: 'rgba(139,69,19,0.3)' },
  { id: 'reentry', label: 'REENTRY', color: '#a0522d', bg: 'rgba(160,82,45,0.12)', border: 'rgba(160,82,45,0.3)' },
  { id: 'spiritual', label: 'SPIRITUAL + CHAPLAINCY', color: '#c9a84c', bg: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.25)' },
  { id: 'education', label: 'EDUCATION IN CUSTODY', color: '#2d8a87', bg: 'rgba(45,138,135,0.1)', border: 'rgba(45,138,135,0.25)' },
  { id: 'family', label: 'FAMILY CONNECTION', color: '#d4c5a9', bg: 'rgba(212,197,169,0.06)', border: 'rgba(212,197,169,0.2)' },
  { id: 'housing', label: 'HOUSING REENTRY', color: '#2d7a47', bg: 'rgba(45,122,71,0.1)', border: 'rgba(45,122,71,0.25)' },
];

// ─── JUSTICE RESOURCES DATA ───────────────────────────────────────────────────
interface JusticeResource {
  id: string;
  name: string;
  city: string;
  categories: string[];
  description: string;
  contact?: string;
  address?: string;
  note?: string;
}

const JUSTICE_RESOURCES: JusticeResource[] = [
  // ATLANTA
  {
    id: 'atl-1',
    name: 'Atlanta Legal Aid Society — Reentry Project',
    city: 'Atlanta',
    categories: ['legal', 'reentry'],
    description: 'Free civil legal services for formerly incarcerated individuals navigating housing, employment, and family law matters post-release.',
    contact: '(404) 524-5811',
    address: '151 Spring St NW, Atlanta, GA',
    note: 'Serves Fulton, DeKalb, Cobb, Gwinnett, Clayton counties',
  },
  {
    id: 'atl-2',
    name: 'Masjid Al-Muminun — Prison Outreach',
    city: 'Atlanta',
    categories: ['spiritual', 'reentry'],
    description: 'Islamic chaplaincy services, Quran and Islamic education for incarcerated Muslims, and reentry support for returning community members.',
    address: 'West End, Atlanta, GA',
    note: 'Jumu\'ah services at Fulton County Jail and state facilities',
  },
  {
    id: 'atl-3',
    name: 'Goodwill of North Georgia — Reentry Services',
    city: 'Atlanta',
    categories: ['reentry', 'housing'],
    description: 'Workforce development, transitional employment, and housing navigation for individuals returning from incarceration.',
    contact: '(404) 420-9900',
    address: '2201 Glenwood Ave SE, Atlanta, GA',
  },
  // CHICAGO
  {
    id: 'chi-1',
    name: 'Cabrini Green Legal Aid — Reentry',
    city: 'Chicago',
    categories: ['legal', 'reentry'],
    description: 'Free legal representation for people with criminal records seeking to clear records, access housing, and restore rights.',
    contact: '(312) 738-2452',
    address: '6 W Hubbard St, Chicago, IL',
  },
  {
    id: 'chi-2',
    name: 'Inner-City Muslim Action Network (IMAN) — Reentry',
    city: 'Chicago',
    categories: ['spiritual', 'reentry', 'housing'],
    description: 'Holistic reentry programming rooted in Islamic values — transitional housing, workforce development, health services, and spiritual support for returning citizens.',
    contact: '(773) 434-4626',
    address: '2744 W 63rd St, Chicago, IL',
    note: 'Flagship reentry program in the Muslim community',
  },
  {
    id: 'chi-3',
    name: 'Chicago Books to Women in Prison',
    city: 'Chicago',
    categories: ['education', 'spiritual'],
    description: 'Free books and educational materials to incarcerated women in Illinois, including Islamic texts and educational resources.',
    address: 'Chicago, IL',
  },
  // DETROIT
  {
    id: 'det-1',
    name: 'Michigan Prisoner ReEntry Initiative (MPRI)',
    city: 'Detroit',
    categories: ['reentry', 'housing'],
    description: 'State-coordinated reentry services connecting returning citizens to housing, employment, substance abuse treatment, and community support.',
    contact: '(517) 335-1426',
    address: 'Detroit, MI',
  },
  {
    id: 'det-2',
    name: 'Muslim Unity Center — Prison Ministry',
    city: 'Detroit',
    categories: ['spiritual', 'education'],
    description: 'Islamic education, Quran classes, and chaplaincy services for incarcerated Muslims in Wayne County and surrounding facilities.',
    address: 'Bloomfield Hills, MI',
    note: 'Serves multiple correctional facilities in Southeast Michigan',
  },
  // PITTSBURGH
  {
    id: 'pit-1',
    name: 'Allegheny County Jail Collaborative',
    city: 'Pittsburgh',
    categories: ['reentry', 'education', 'legal'],
    description: 'Comprehensive reentry services including GED programs, vocational training, legal aid, and housing navigation for individuals at Allegheny County Jail.',
    contact: '(412) 350-2000',
    address: '950 Second Ave, Pittsburgh, PA',
  },
  {
    id: 'pit-2',
    name: 'Islamic Center of Pittsburgh — Reentry Support',
    city: 'Pittsburgh',
    categories: ['spiritual', 'reentry'],
    description: 'Community-based support for returning Muslim citizens including mentorship, spiritual guidance, and connection to community resources.',
    address: '4100 Bigelow Blvd, Pittsburgh, PA',
  },
  // DURHAM
  {
    id: 'dur-1',
    name: 'NC Prisoner Legal Services',
    city: 'Durham',
    categories: ['legal'],
    description: 'Free civil legal services to incarcerated individuals in North Carolina state prisons, including appeals, conditions of confinement, and reentry planning.',
    contact: '(919) 856-2200',
    address: 'Raleigh, NC (serves Durham region)',
  },
  {
    id: 'dur-2',
    name: 'Bull City Reentry — Durham',
    city: 'Durham',
    categories: ['reentry', 'housing', 'education'],
    description: 'Peer-led reentry support, transitional housing referrals, GED preparation, and workforce development for Durham County returning citizens.',
    address: 'Durham, NC',
  },
  // LOUISVILLE
  {
    id: 'lou-1',
    name: 'Louisville Islamic Education Center — Prison Outreach',
    city: 'Louisville',
    categories: ['spiritual', 'education'],
    description: 'Islamic education and chaplaincy services for incarcerated Muslims at Louisville Metro Corrections and Kentucky state facilities.',
    address: 'Louisville, KY',
  },
  {
    id: 'lou-2',
    name: 'Louisville Reentry Initiative',
    city: 'Louisville',
    categories: ['reentry', 'legal', 'housing'],
    description: 'Coordinated reentry services including legal aid, housing placement, employment support, and family reunification services.',
    contact: '(502) 574-5700',
    address: 'Louisville, KY',
  },
  // PORTLAND
  {
    id: 'por-1',
    name: 'Oregon Law Center — Reentry Project',
    city: 'Portland',
    categories: ['legal', 'reentry'],
    description: 'Free legal services for formerly incarcerated individuals in Oregon, including record expungement, housing rights, and family law.',
    contact: '(503) 473-8321',
    address: 'Portland, OR',
  },
  {
    id: 'por-2',
    name: 'Bilal Mosque — Reentry & Chaplaincy',
    city: 'Portland',
    categories: ['spiritual', 'reentry'],
    description: 'Islamic chaplaincy at Oregon correctional facilities and community reentry support for returning Muslim citizens in the Portland metro area.',
    address: 'Portland, OR',
  },
  // CINCINNATI
  {
    id: 'cin-1',
    name: 'Legal Aid Society of Greater Cincinnati — Reentry',
    city: 'Cincinnati',
    categories: ['legal', 'reentry'],
    description: 'Free legal services for low-income returning citizens including record sealing, housing discrimination defense, and benefits restoration.',
    contact: '(513) 241-9400',
    address: '215 E 9th St, Cincinnati, OH',
  },
  {
    id: 'cin-2',
    name: 'Islamic Center of Greater Cincinnati — Prison Ministry',
    city: 'Cincinnati',
    categories: ['spiritual', 'education'],
    description: 'Quran education, Islamic studies, and chaplaincy services for incarcerated Muslims in Hamilton County and surrounding Ohio facilities.',
    address: '8092 Brownsboro Rd, Cincinnati, OH',
  },
  // OMAHA
  {
    id: 'oma-1',
    name: 'Nebraska Appleseed — Reentry Justice',
    city: 'Omaha',
    categories: ['legal', 'reentry'],
    description: 'Policy advocacy and direct legal services for formerly incarcerated individuals in Nebraska, focusing on housing, employment, and civic rights restoration.',
    contact: '(402) 438-8853',
    address: '1321 Jones St, Omaha, NE',
  },
  {
    id: 'oma-2',
    name: 'Islamic Foundation of Greater Omaha — Reentry',
    city: 'Omaha',
    categories: ['spiritual', 'reentry'],
    description: 'Community support, Islamic education, and chaplaincy services for incarcerated and returning Muslim community members in the Omaha metro area.',
    address: 'Omaha, NE',
  },
];

const ALL_CITIES = ['All Cities', 'Atlanta', 'Chicago', 'Detroit', 'Pittsburgh', 'Durham', 'Louisville', 'Portland', 'Cincinnati', 'Omaha'];

export default function JusticeInvolvedSection() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeCity, setActiveCity] = useState<string>('All Cities');

  const filtered = JUSTICE_RESOURCES.filter(r => {
    const catMatch = activeCategory === 'all' || r.categories.includes(activeCategory);
    const cityMatch = activeCity === 'All Cities' || r.city === activeCity;
    return catMatch && cityMatch;
  });

  return (
    <section id="justice-involved" style={{
      background: '#060d06',
      borderTop: '1px solid rgba(139,69,19,0.2)',
      padding: '64px 24px',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Featured Banner */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(139,69,19,0.15), rgba(201,168,76,0.08))',
          border: '1px solid rgba(139,69,19,0.3)',
          borderRadius: 8,
          padding: '20px 24px',
          marginBottom: 32,
        }}>
          <div style={{ fontSize: 11, color: '#8b4513', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            RESOURCES FOR IMPRISONED CARETAKERS
          </div>
          <p style={{ fontSize: 12, color: '#d4c5a9', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 10 }}>
            "Commanded to come down for a time as Caretakers...yet, forgetful as we are, we're entrusted particular guidance."
          </p>
          <Link href="/open-letter" style={{ fontSize: 11, color: '#c9a84c', textDecoration: 'none' }}>
            From the Open Letter — Read in context →
          </Link>
        </div>

        {/* Section Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 9, color: '#8b4513', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
            INCARCERATED + REENTRY
          </div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 28, color: '#d4c5a9', marginBottom: 8 }}>
            Justice-Involved Community Resources
          </h2>
          <p style={{ fontSize: 13, color: '#7a6f5d', lineHeight: 1.6, maxWidth: 700 }}>
            Resources serving imprisoned, formerly incarcerated, and justice-involved communities across all 9 cities. 
            Legal support, spiritual care, education in custody, reentry services, family connection, and housing navigation.
          </p>
        </div>

        {/* Category Badges */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
            FILTER BY CATEGORY
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              onClick={() => setActiveCategory('all')}
              style={{
                padding: '6px 14px',
                background: activeCategory === 'all' ? 'rgba(212,197,169,0.15)' : 'transparent',
                border: `1px solid ${activeCategory === 'all' ? 'rgba(212,197,169,0.4)' : 'rgba(212,197,169,0.15)'}`,
                borderRadius: 3,
                fontSize: 10,
                color: '#d4c5a9',
                cursor: 'pointer',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                transition: 'all 0.2s',
              }}
            >
              ALL RESOURCES
            </button>
            {JUSTICE_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  padding: '6px 14px',
                  background: activeCategory === cat.id ? cat.bg : 'transparent',
                  border: `1px solid ${activeCategory === cat.id ? cat.border : 'rgba(139,69,19,0.15)'}`,
                  borderRadius: 3,
                  fontSize: 10,
                  color: activeCategory === cat.id ? cat.color : '#7a6f5d',
                  cursor: 'pointer',
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  transition: 'all 0.2s',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* City Filter */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
            FILTER BY CITY
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ALL_CITIES.map(city => (
              <button
                key={city}
                onClick={() => setActiveCity(city)}
                style={{
                  padding: '5px 12px',
                  background: activeCity === city ? 'rgba(139,69,19,0.15)' : 'transparent',
                  border: `1px solid ${activeCity === city ? 'rgba(139,69,19,0.4)' : 'rgba(139,69,19,0.1)'}`,
                  borderRadius: 3,
                  fontSize: 10,
                  color: activeCity === city ? '#c9a84c' : '#7a6f5d',
                  cursor: 'pointer',
                  letterSpacing: '.06em',
                  transition: 'all 0.2s',
                }}
              >
                {city}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', marginBottom: 20 }}>
          {filtered.length} RESOURCE{filtered.length !== 1 ? 'S' : ''} — {activeCity.toUpperCase()} · {activeCategory === 'all' ? 'ALL CATEGORIES' : JUSTICE_CATEGORIES.find(c => c.id === activeCategory)?.label}
        </div>

        {/* Resource Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {filtered.map(resource => (
            <div key={resource.id} style={{
              background: 'rgba(6,13,6,0.8)',
              border: '1px solid rgba(139,69,19,0.15)',
              borderRadius: 6,
              padding: 20,
              transition: 'border-color 0.2s',
            }}>
              {/* Category badges */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                {resource.categories.map(catId => {
                  const cat = JUSTICE_CATEGORIES.find(c => c.id === catId);
                  if (!cat) return null;
                  return (
                    <span key={catId} style={{
                      fontSize: 8,
                      color: cat.color,
                      background: cat.bg,
                      border: `1px solid ${cat.border}`,
                      borderRadius: 2,
                      padding: '2px 6px',
                      letterSpacing: '.08em',
                      textTransform: 'uppercase',
                    }}>
                      {cat.label}
                    </span>
                  );
                })}
                <span style={{
                  fontSize: 8,
                  color: '#7a6f5d',
                  background: 'rgba(122,111,93,0.08)',
                  border: '1px solid rgba(122,111,93,0.15)',
                  borderRadius: 2,
                  padding: '2px 6px',
                  letterSpacing: '.06em',
                }}>
                  {resource.city.toUpperCase()}
                </span>
              </div>

              {/* Name */}
              <h3 style={{ fontSize: 14, color: '#d4c5a9', marginBottom: 8, lineHeight: 1.4, fontFamily: "'Georgia', serif" }}>
                {resource.name}
              </h3>

              {/* Description */}
              <p style={{ fontSize: 12, color: '#9a8f7d', lineHeight: 1.6, marginBottom: 12 }}>
                {resource.description}
              </p>

              {/* Contact info */}
              {(resource.address || resource.contact) && (
                <div style={{ borderTop: '1px solid rgba(139,69,19,0.1)', paddingTop: 10, marginTop: 10 }}>
                  {resource.address && (
                    <div style={{ fontSize: 10, color: '#7a6f5d', marginBottom: 3 }}>📍 {resource.address}</div>
                  )}
                  {resource.contact && (
                    <div style={{ fontSize: 10, color: '#7a6f5d', marginBottom: 3 }}>📞 {resource.contact}</div>
                  )}
                  {resource.note && (
                    <div style={{ fontSize: 10, color: '#8b4513', fontStyle: 'italic', marginTop: 6 }}>
                      ✦ {resource.note}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#7a6f5d', fontSize: 13 }}>
            No resources found for the selected filters. Try selecting "All Cities" or a different category.
          </div>
        )}

        {/* Footer note */}
        <div style={{
          marginTop: 40,
          padding: '20px 24px',
          background: 'rgba(139,69,19,0.06)',
          border: '1px solid rgba(139,69,19,0.15)',
          borderRadius: 6,
        }}>
          <div style={{ fontSize: 9, color: '#8b4513', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
            ABOUT THIS SECTION
          </div>
          <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.6 }}>
            These resources are compiled for imprisoned caretakers, their families, and community members supporting reentry. 
            The Haal Project recognizes that the Amanah — the trust — of caretaking extends to those behind walls. 
            Islah begins with recognizing what is broken and bridging it. Fisabilillah.
          </p>
          <div style={{ marginTop: 12 }}>
            <Link href="/open-letter#section-al-qadir" style={{ fontSize: 11, color: '#c9a84c', textDecoration: 'none', marginRight: 16 }}>
              → Read the Open Letter
            </Link>
            <Link href="/keys-to-kulli-haal" style={{ fontSize: 11, color: '#2d7a47', textDecoration: 'none' }}>
              → Keys to Kulli Haal
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
